import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'api/api-context';
import BookingAccountLayout from 'pages/booking/account/BookingAccountLayout';
import { AccountError, AccountSkeleton } from 'pages/booking/account/account-utils';
import {
  BOOKING_ACCOUNT_CREDITS_PATH,
  BOOKING_ACCOUNT_CREDIT_PURCHASES_PATH,
  BOOKING_ACCOUNT_INVOICE_DETAIL_PATH,
} from 'routes/paths';
import useLang from 'lang';
import { formatLocaleDateTime } from 'lang/date-time';
import { creditPurchaseStatusLabel } from 'pages/booking/account/credit-purchase-status';
import IconArrowLeft from 'assets/arrow-narrow-left.svg';

const UNPAID_STATES = ['initializing', 'waiting', 'processing', 'failed'];

/** Purchase status, payment actions, expiry, and invoice access. */
export default function AccountCreditPurchaseDetail() {
  const { purchaseId = '' } = useParams<{ purchaseId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { getAction } = useApi();
  const { _t, lang } = useLang();
  const [purchase, setPurchase] = useState<any>((location.state as any)?.purchase ?? null);
  const [loading, setLoading] = useState(!purchase);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response: any = await getAction('GetReservationCreditPurchase')({
        parameters: { purchaseId: Number(purchaseId) },
      });
      setPurchase(response.purchase);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [getAction, purchaseId]);

  useEffect(() => {
    if (!purchase) void load();
  }, [load, purchase]);

  /** Refreshes the gateway state and rendered purchase. */
  const refresh = async () => {
    setBusy(true);
    try {
      const response: any = await getAction('RefreshReservationCreditPurchase')({
        parameters: { purchaseId: Number(purchaseId) },
      });
      setPurchase(response.purchase);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  /** Cancels an unpaid purchase. */
  const cancel = async () => {
    if (!window.confirm(_t('Cancel this unpaid credit purchase?'))) return;
    setBusy(true);
    try {
      const response: any = await getAction('CancelReservationCreditPurchase')({
        parameters: { purchaseId: Number(purchaseId) },
      });
      setPurchase(response.purchase);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <BookingAccountLayout>
      <button className="back-btn" onClick={() => navigate(BOOKING_ACCOUNT_CREDIT_PURCHASES_PATH.original)}>
        <img src={IconArrowLeft} width={20} height={20} alt="" />
        {_t('Back to purchases')}
      </button>
      {loading && <AccountSkeleton />}
      {error && !purchase && <AccountError retry={() => void load()} />}
      {purchase && (
        <article className="account-detail-card">
          <div className="account-card-heading">
            <div>
              <p className="account-eyebrow">
                {_t('Credit purchase')} #{purchase.id}
              </p>
              <h2>{purchase.space.name}</h2>
            </div>
            <span className={`account-status account-status--${purchase.status}`}>
              {_t(creditPurchaseStatusLabel(purchase.status))}
            </span>
          </div>
          <dl className="account-detail-list">
            <div>
              <dt>{_t('Credits')}</dt>
              <dd>{purchase.credits}</dd>
            </div>
            <div>
              <dt>{_t('Total including VAT')}</dt>
              <dd>
                {Number(purchase.amountWithVat).toFixed(2)} {purchase.currencyCode}
              </dd>
            </div>
            <div>
              <dt>{_t('VAT')}</dt>
              <dd>
                {Number(purchase.vat).toFixed(2)} {purchase.currencyCode} ({purchase.vatPercentage}%)
              </dd>
            </div>
            <div>
              <dt>{_t('Payment method')}</dt>
              <dd>{_t(purchase.paymentGateway === 'stripe' ? 'Stripe' : 'Manual')}</dd>
            </div>
            <div>
              <dt>{_t('Purchased')}</dt>
              <dd>{formatLocaleDateTime(purchase.createdAt, lang)}</dd>
            </div>
            <div>
              <dt>{_t('Credit expiry')}</dt>
              <dd>
                {purchase.expiresAt ? formatLocaleDateTime(purchase.expiresAt, lang) : _t('Starts after payment')}
              </dd>
            </div>
          </dl>
          {purchase.paymentGateway === 'manual' && purchase.status === 'waiting' && (
            <div className="account-finance-card">
              <strong>{_t('Waiting for bank transfer')}</strong>
              <p>
                {_t(
                  'Use the issued invoice for payment details. Credits are added after an administrator confirms payment.'
                )}
              </p>
            </div>
          )}
          {purchase.status === 'paid' && (
            <div className="account-finance-card">
              <strong>{_t('Credits purchased')}</strong>
              <p>{_t('Your credits are now available and will be used before paid booking slots.')}</p>
            </div>
          )}
          {error && (
            <p className="account-state--error" role="alert">
              {_t('The latest purchase status could not be loaded.')}
            </p>
          )}
          <div className="account-actions">
            {purchase.attempt?.checkoutUrl && UNPAID_STATES.includes(purchase.status) && (
              <button className="account-button" onClick={() => window.location.assign(purchase.attempt.checkoutUrl)}>
                {_t('Continue card payment')}
              </button>
            )}
            {UNPAID_STATES.includes(purchase.status) && (
              <button
                className="account-button account-button--secondary"
                disabled={busy}
                onClick={() => void refresh()}
              >
                {_t('Refresh payment status')}
              </button>
            )}
            {UNPAID_STATES.includes(purchase.status) && (
              <button className="account-button account-button--danger" disabled={busy} onClick={() => void cancel()}>
                {_t('Cancel purchase')}
              </button>
            )}
            {purchase.invoice?.id && purchase.invoice.issuanceStatus === 'issued' && (
              <button
                className="account-button account-button--secondary"
                onClick={() =>
                  navigate(BOOKING_ACCOUNT_INVOICE_DETAIL_PATH.insert({ invoiceId: String(purchase.invoice.id) }))
                }
              >
                {_t('View invoice PDF')}
              </button>
            )}
            {purchase.status === 'paid' && (
              <button className="account-button" onClick={() => navigate(BOOKING_ACCOUNT_CREDITS_PATH.original)}>
                {_t('View credit balance')}
              </button>
            )}
          </div>
        </article>
      )}
    </BookingAccountLayout>
  );
}
